/**
 * @module VerifyPolling
 * @description Phase 14 extraction of abort-aware quick-test polling utilities.
 * Coordinates retry timing and cancellation for all verify strategies.
 */

import { t } from '@lingui/macro';
import { ALL_QUICKTEST_STATUSES } from './VerifySharedConstants';

const QUICKTEST_API = '/ipm/v1/admin/quicktests';

// portal/ui source: QuickTestsService.getQuickTestById() passes all status values
// so the API returns the quicktest record regardless of its current status.
const QUICK_TEST_QUERY_PARAMS = {
    query: JSON.stringify({ status: ALL_QUICKTEST_STATUSES }),
};

/**
 * Abort-aware sleep. Rejects with AbortError if signal is already aborted
 * or becomes aborted during the wait.
 */
const sleep = (ms, signal) =>
    new Promise((resolve, reject) => {
        if (signal?.aborted) {
            reject(new DOMException('Aborted', 'AbortError'));
            return;
        }
        const timer = setTimeout(resolve, ms);
        if (signal) {
            signal.addEventListener(
                'abort',
                () => {
                    clearTimeout(timer);
                    reject(new DOMException('Aborted', 'AbortError'));
                },
                {
                    once: true,
                }
            );
        }
    });

/**
 * Default polling counter — static poll count based on timeout.
 * portal/ui source: verify.service.js defaultPollingCount()
 *
 * @param {Object} strategy
 * @returns {Function} (pollState) => pollState
 */
function defaultPollingCounter(strategy) {
    return (pollState) => {
        pollState.pollFor = (pollState.testTypeTimeout * 1000) / strategy.pollConfig.pollIntervalMs + 10;
        return pollState;
    };
}

/**
 * Handle poll timeout — show failure results or timeout message.
 */
function handlePollTimeout({ strategy, getAgentName, setVerifyResult }, response) {
    if (strategy.verifyHasResults(response)) {
        setVerifyResult(strategy.buildVerifyResult(false, response, getAgentName));
    } else {
        setVerifyResult({
            status: t`Failure`,
            statusMsg: t`Tests have not completed before timeout. If this problem persists, please contact support.`,
            resultType: null,
            resultData: null,
        });
    }
}

/**
 * Update UI with processing status when partial results are available.
 */
function updateProcessingStatus({ strategy, setVerifyResult }, response) {
    if (strategy.verifyHasResults(response)) {
        setVerifyResult({
            status: t`Processing`,
            statusMsg: t`The test is running. Waiting for final results...`,
            resultType: null,
            resultData: null,
        });
    }
}

/**
 * Execute a single poll iteration and recurse if still running.
 * Async recursion avoids while-loop lint issues; stack depth is bounded
 * by strategy-specific pollFor counter (typically 55–910 iterations).
 *
 * @param {Object} ctx - Polling context with all dependencies
 * @returns {Promise<void>}
 */
async function executePollIteration(ctx) {
    if (ctx.signal?.aborted) return;

    const response = await ctx.dataService.get(`${QUICKTEST_API}/${ctx.verifyId}`, QUICK_TEST_QUERY_PARAMS, {
        signal: ctx.signal,
    });
    ctx.pollState.response = response;

    const { isSuccessful, stillRunning } = ctx.strategy.checkStatus(response);

    // Success — build and return result
    if (isSuccessful) {
        ctx.setVerifyResult(ctx.strategy.buildVerifyResult(true, response, ctx.getAgentName));
        return;
    }

    // Update dynamic poll counter
    ctx.pollState = ctx.pollForCounter(ctx.pollState);

    // Timeout check
    if (ctx.pollState.pollCount > ctx.pollState.pollFor) {
        handlePollTimeout(ctx, response);
        return;
    }

    // Not running, not successful — show failure results
    if (!stillRunning) {
        ctx.setVerifyResult(ctx.strategy.buildVerifyResult(false, response, ctx.getAgentName));
        return;
    }

    // Still running — continue polling
    ctx.pollState.pollCount += 1;
    updateProcessingStatus(ctx, response);
    await sleep(ctx.pollIntervalMs, ctx.signal);
    return executePollIteration(ctx);
}

/**
 * Poll the quicktest API for verify results.
 * portal/ui source: verify.service.js pollForVerify()
 *
 * Delegates to recursive executePollIteration() to mirror portal/ui's
 * $timeout recursion pattern while satisfying lint rules.
 */
async function pollForResults({ verifyId, strategy, testType, getAgentName, dataService, setVerifyResult, signal }) {
    const pollForCounter = strategy.pollingCounter || defaultPollingCounter(strategy);
    const { pollIntervalMs } = strategy.pollConfig;

    // portal/ui source: testsDetails.controller.js line 1262 passes testType.details.testTimeOutSec
    // as the timeout parameter to VerifyService.verify(). Default fallback is 45 seconds.
    const testTypeTimeout = testType?.details?.testTimeOutSec || strategy.pollConfig.pollFor;

    const ctx = {
        verifyId,
        strategy,
        getAgentName,
        dataService,
        setVerifyResult,
        signal,
        pollForCounter,
        pollIntervalMs,
        pollState: {
            response: null,
            testType,
            testTypeTimeout,
            pollCount: 0,
            pollFor: 0,
            hasStarted: false,
        },
    };

    return executePollIteration(ctx);
}

export { pollForResults };
